import { api } from './api'
import { getAll as list } from './pagination'

export interface StudentOption { id: number; student_id: string; full_name: string; class_name?: string; class_level_order?: number | null }
export interface StaffOption { id: number; full_name: string; role: string }

export interface BehaviorCategory {
  id: number; name: string; description: string; severity_level: number;
  color_code: string; is_active: boolean; incident_count: number
}

export interface DisciplinaryAction {
  id: number; name: string; action_type: string; description: string;
  severity_level: number; duration_days: number; requires_parent_notification: boolean;
  requires_principal_approval: boolean; is_active: boolean; incident_count: number
}

export interface BehaviorIncident {
  id: number; student: number; student_name: string; student_id: string; student_class?: string;
  reported_by: number; reported_by_name: string; category: number; category_name: string; category_color: string;
  action_taken: number; action_taken_name: string; action_type: string; action_needs_parent_notice: boolean;
  title: string; description: string;
  incident_date: string; location: string; witnesses: string; severity_level: number;
  status: 'reported' | 'investigating' | 'resolved' | 'closed' | 'escalated';
  action_date?: string; action_duration: number; action_notes: string;
  follow_up_required: boolean; follow_up_date?: string; follow_up_notes: string;
  parent_notified: boolean; parent_notification_date?: string; parent_response: string;
  requires_approval: boolean; approval_status: 'not_required' | 'pending' | 'approved' | 'rejected';
  approved_by_name?: string; approval_date?: string; approval_notes: string;
  approval_rejection_reason: string; approval_rejected_by_name: string; approval_rejected_at?: string;
  escalation_reason: string; escalated_by_name: string; escalated_at?: string;
  investigated_by_name: string; investigation_started_at?: string;
  resolved_by_name: string; resolved_at?: string; closed_by_name: string; closed_at?: string;
  parent_notified_by_name: string; is_confidential: boolean; created_at: string
}

export interface InterventionReview {
  id: number; reviewed_by: number; reviewed_by_name: string; reviewed_at: string;
  progress_notes: string; progress_rating: number; next_review_date?: string
}

export interface InterventionAction {
  id: number; intervention_plan: number; action_type: string; title: string; description: string;
  responsible_staff: number; responsible_staff_name: string; scheduled_date: string;
  completed_date?: string; duration_minutes: number;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'rescheduled';
  outcome_notes: string; effectiveness_rating?: number; follow_up_required: boolean;
  follow_up_date?: string; follow_up_notes: string; cancellation_reason: string;
  reschedule_reason: string
}

export interface InterventionPlan {
  id: number; student: number; student_name: string; student_id: string; student_class: string;
  created_by: number; created_by_name: string; title: string; description: string;
  plan_type: 'preventive' | 'corrective' | 'supportive' | 'comprehensive';
  status: 'draft' | 'active' | 'completed' | 'suspended' | 'cancelled';
  start_date: string; end_date: string; review_frequency_days: number; primary_goal: string;
  specific_objectives: string[]; success_metrics: string[]; responsible_staff: number[];
  responsible_staff_names: string[]; parent_involvement: boolean; parent_consent: boolean;
  parent_consent_date?: string; last_review_date?: string; next_review_date?: string;
  outcome_summary: string; success_rate?: number; lessons_learned: string;
  suspension_reason: string; cancellation_reason: string; created_at: string;
  activated_at?: string; completed_at?: string; suspended_at?: string; cancelled_at?: string;
  actions: InterventionAction[]; reviews: InterventionReview[]
}

export interface BehaviorPattern {
  id: number; student: number; student_name: string; student_id: string; student_class: string;
  category: number; category_name: string; incident_count: number; average_severity: number;
  trend_direction: 'improving' | 'stable' | 'worsening'; risk_level: number;
  intervention_required: boolean; analysis_start_date: string; analysis_end_date: string
}

export interface DisciplinarySummary {
  total_incidents: number; total_students_with_incidents: number;
  incidents_this_month: number; incidents_last_month: number; pending_incidents: number;
  resolved_incidents: number; active_interventions: number; high_risk_students: number
}

export interface ClassDisciplinarySummary {
  class_id: number; class_name: string; total_students: number; students_with_incidents: number;
  total_incidents: number; incidents_this_month: number; average_severity: number;
  most_common_category: string; active_interventions: number
}

export interface BehaviorAnalytics {
  total_incidents: number; incidents_by_severity: Record<string, number>;
  incidents_by_category: Record<string, number>; incidents_by_month: Array<Record<string, unknown>>;
  top_offending_students: Array<Record<string, unknown>>;
  intervention_effectiveness: { total_plans: number; successful_plans: number; average_success_rate: number };
  trend_analysis: { direction: string; recent_incidents: number; previous_incidents: number }
}

export interface DisciplinaryReport {
  id: number; name: string; report_type: string; generated_at: string;
  student_filter?: number | null; student_filter_name?: string; class_filter?: number | null; class_filter_name?: string;
  category_filter?: number | null; category_filter_name?: string; date_from?: string | null; date_to?: string | null;
  generated_by_name: string; total_incidents: number; total_students: number;
  severity_breakdown: Record<string, number>; category_breakdown: Record<string, number>;
  trend_data: Record<string, unknown>
}

export const disciplineService = {
  getStudents: () => list<StudentOption>('/students/service-directory/'),
  getStaff: async () => (await api.get('/discipline/staff-directory/')).data as StaffOption[],

  getBehaviorCategories: () => list<BehaviorCategory>('/discipline/categories/'),
  createBehaviorCategory: async (data: Partial<BehaviorCategory>) => (await api.post('/discipline/categories/', data)).data as BehaviorCategory,
  updateBehaviorCategory: async ({ id, data }: { id: number; data: Partial<BehaviorCategory> }) => (await api.patch(`/discipline/categories/${id}/`, data)).data as BehaviorCategory,
  getDisciplinaryActions: () => list<DisciplinaryAction>('/discipline/actions/'),
  createDisciplinaryAction: async (data: Partial<DisciplinaryAction>) => (await api.post('/discipline/actions/', data)).data as DisciplinaryAction,
  updateDisciplinaryAction: async ({ id, data }: { id: number; data: Partial<DisciplinaryAction> }) => (await api.patch(`/discipline/actions/${id}/`, data)).data as DisciplinaryAction,

  getBehaviorIncidents: () => list<BehaviorIncident>('/discipline/incidents/'),
  createBehaviorIncident: async (data: Partial<BehaviorIncident>) => (await api.post('/discipline/incidents/', data)).data as BehaviorIncident,
  investigateIncident: async (id: number) => (await api.post(`/discipline/incidents/${id}/investigate/`)).data as BehaviorIncident,
  escalateIncident: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/discipline/incidents/${id}/escalate/`, { reason })).data as BehaviorIncident,
  approveIncident: async ({ id, notes }: { id: number; notes: string }) => (await api.post(`/discipline/incidents/${id}/approve/`, { notes })).data as BehaviorIncident,
  rejectIncidentApproval: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/discipline/incidents/${id}/reject-approval/`, { reason })).data as BehaviorIncident,
  notifyParent: async (id: number) => (await api.post(`/discipline/incidents/${id}/notify-parent/`)).data as { notified_count: number; incident: BehaviorIncident },
  recordParentResponse: async ({ id, response }: { id: number; response: string }) => (await api.post(`/discipline/incidents/${id}/parent-response/`, { response })).data as BehaviorIncident,
  resolveIncident: async ({ id, data }: { id: number; data: Record<string, unknown> }) => (await api.post(`/discipline/incidents/${id}/resolve/`, data)).data as BehaviorIncident,
  closeIncident: async (id: number) => (await api.post(`/discipline/incidents/${id}/close/`)).data as BehaviorIncident,

  getInterventionPlans: () => list<InterventionPlan>('/discipline/interventions/'),
  createInterventionPlan: async (data: Partial<InterventionPlan>) => (await api.post('/discipline/interventions/', data)).data as InterventionPlan,
  recordParentConsent: async (id: number) => (await api.post(`/discipline/interventions/${id}/parent-consent/`)).data as InterventionPlan,
  activateIntervention: async (id: number) => (await api.post(`/discipline/interventions/${id}/activate/`)).data as InterventionPlan,
  reviewIntervention: async ({ id, data }: { id: number; data: Record<string, unknown> }) => (await api.post(`/discipline/interventions/${id}/review/`, data)).data as InterventionReview,
  suspendIntervention: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/discipline/interventions/${id}/suspend/`, { reason })).data as InterventionPlan,
  resumeIntervention: async (id: number) => (await api.post(`/discipline/interventions/${id}/resume/`)).data as InterventionPlan,
  completeIntervention: async ({ id, data }: { id: number; data: Record<string, unknown> }) => (await api.post(`/discipline/interventions/${id}/complete/`, data)).data as InterventionPlan,
  cancelIntervention: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/discipline/interventions/${id}/cancel/`, { reason })).data as InterventionPlan,

  createInterventionAction: async (data: Partial<InterventionAction>) => (await api.post('/discipline/intervention-actions/', data)).data as InterventionAction,
  startInterventionAction: async (id: number) => (await api.post(`/discipline/intervention-actions/${id}/start/`)).data as InterventionAction,
  completeInterventionAction: async ({ id, data }: { id: number; data: Record<string, unknown> }) => (await api.post(`/discipline/intervention-actions/${id}/complete/`, data)).data as InterventionAction,
  cancelInterventionAction: async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/discipline/intervention-actions/${id}/cancel/`, { reason })).data as InterventionAction,
  rescheduleInterventionAction: async ({ id, data }: { id: number; data: Record<string, unknown> }) => (await api.post(`/discipline/intervention-actions/${id}/reschedule/`, data)).data as InterventionAction,

  getBehaviorPatterns: () => list<BehaviorPattern>('/discipline/patterns/'),
  getDisciplinarySummary: async () => (await api.get('/discipline/summary/')).data as DisciplinarySummary,
  getClassDisciplinarySummary: async () => (await api.get('/discipline/class-summary/')).data as ClassDisciplinarySummary[],
  getBehaviorAnalytics: async () => (await api.get('/discipline/analytics/')).data as BehaviorAnalytics,
  getDisciplinaryReports: () => list<DisciplinaryReport>('/discipline/reports/'),
  createDisciplinaryReport: async (data: Partial<DisciplinaryReport> & Record<string, unknown>) => (await api.post('/discipline/reports/', data)).data as DisciplinaryReport,
}
